import { cn } from "@faire/react/tools/cn";

import { TAB_PANEL_NAME_PREFIX } from "./consts";

/**
 * Get the id of a tab panel.
 *
 * @param value - The value of the tab.
 * @returns The id of the panel controlled by the tab.
 */
export const getTabPanelId = (value: string) =>
  `${TAB_PANEL_NAME_PREFIX}-${value}`;

/**
 * The options for getting the props for a tab panel.
 */
interface IGetTabPanelPropsOptions<T extends string> {
  /**
   * The value of the tab that is associated with the panel.
   */
  tabValue: T;
  /**
   * The value of the selected tab.
   */
  selectedTabValue?: T;
  /**
   * Additional class names to apply to the tab panel.
   */
  className?: string;
}

/**
 * Get the props for a tab panel.
 *
 * @remarks
 * This function is used to generate the props for a tab panel making it
 * accessible to users. It also controls the visibility of the panel based on
 * the selected tab.
 *
 * @param options - The options for the tab panel.
 * @returns The props for the tab panel.
 */
export function getTabPanelProps<T extends string>({
  tabValue,
  selectedTabValue,
  className,
}: IGetTabPanelPropsOptions<T>) {
  return {
    "aria-labelledby": tabValue,
    className: cn(className, { hidden: selectedTabValue !== tabValue }),
    id: getTabPanelId(tabValue),
    role: "tabpanel",
    "aria-hidden": selectedTabValue !== tabValue,
  };
}
